// Updated SandboxTrainDetail.tsx with fixed chart lines, white axis and grid
import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, CardContent, Typography, Box, Paper,
  MenuItem, Select, FormControl, InputLabel, CircularProgress
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer ,Brush
} from 'recharts';
import useDeviceData from './hooks/useDeviceData';
import { format } from 'date-fns';

const processTrendData = (readings: any[]) => {
  const trendData = readings.reduce((acc, reading) => {
    const { ats, v, di, dt } = reading;
    const deviceId = di;
    const value = v;

    if (!acc[deviceId]) {
      acc[deviceId] = {
        display_type: dt.toUpperCase(),
        series_type: dt === 'fault' ? 'discrete' : 'continuous',
        device: {
          hardware_device_id: di,
          position: `${di} Position`,
          hardware: { firmware_version: 53 }
        },
        trend_data: []
      };
    }

    const existing = acc[deviceId].trend_data.find(entry => entry.key === ats);
    if (existing) {
      if (value !== null) {
        existing.min = Math.min(existing.min || value, value);
        existing.max = Math.max(existing.max || value, value);
        existing.values.push(value);
      }
    } else {
      acc[deviceId].trend_data.push({
        key: ats,
        min: value,
        max: value,
        values: [value],
        gap: false,
        formattedTime: format(new Date(ats), 'yyyy-MM-dd HH:mm:ss')
      });
    }

    return acc;
  }, {});

  Object.keys(trendData).forEach(deviceId => {
    trendData[deviceId].trend_data.forEach(entry => {
      const total = entry.values.reduce((sum, val) => sum + val, 0);
      entry.avg = Math.min(total / entry.values.length, 100);
    });
  });

  return trendData;
};

const SandboxTrainDetail: React.FC<{ deviceIds?: string[], sandboxDetail: any[] }> = ({
  deviceIds = [],
  sandboxDetail
}) => {
  const [timeRange, setTimeRange] = useState('last7Days');
  const [data, setData] = useState<any[]>([]);
  const [combinedData, setCombinedData] = useState<any>({});
  const [highlightedDeviceId, setHighlightedDeviceId] = useState<string | null>(null);

  const externalIds = useMemo(() =>
    sandboxDetail.map((item: any) => item.datastream.device_external_id), [sandboxDetail]);

  const deviceMetaMap = useMemo(() => {
    const map: Record<string, { position: string, color: string }> = {};
    sandboxDetail.forEach(item => {
      map[item.datastream.hardware_device_id] = {
        position: item.datastream.labels.position,
        color: item.color
      };
    });
    return map;
  }, [sandboxDetail]);

  const { data: fetchedData, isLoading } = useDeviceData(externalIds, timeRange);

  useEffect(() => {
    if (fetchedData && fetchedData.length > 0) {
      setData(fetchedData);
    }
  }, [fetchedData]);

  useEffect(() => {
    if (data && data.length > 0) {
      const processed = processTrendData(data.flatMap(d => d.readings));
      setCombinedData(processed);
    }
  }, [data]);

  const chartData = useMemo(() => {
    const allTimes = new Set<string>();
    Object.values(combinedData).forEach(device =>
      device.trend_data.forEach(entry => allTimes.add(entry.formattedTime))
    );
    const sortedTimes = Array.from(allTimes).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );
    return sortedTimes.map(time => {
      const point: any = { date: time };
      Object.entries(combinedData).forEach(([deviceId, device]) => {
        const found = device.trend_data.find(e => e.formattedTime === time);
        point[deviceId] = found?.avg ?? null;
      });
      return point;
    }).slice(-50);
  }, [combinedData]);

  
  const onLegendClick= (e)=>{
    const clickedId = e.dataKey;
    setHighlightedDeviceId(prev => prev === clickedId ? null : clickedId);
  }
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom align="center">
          SandBox Level Trends
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <FormControl variant="outlined" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={e => setTimeRange(e.target.value)}
              label="Time Range"
              sx={{ fontSize: '0.75rem', padding: '4px 8px', height: '36px' }}
            >
              <MenuItem value="last60Minutes">Last 60 Minutes</MenuItem>
              <MenuItem value="last12Hours">Last 12 Hours</MenuItem>
              <MenuItem value="last3Days">Last 3 Days</MenuItem>
              <MenuItem value="last7Days">Last 7 Days</MenuItem>
              <MenuItem value="last30Days">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Paper style={{ padding: '16px', height: 300 }}>
          {isLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <CircularProgress size={36} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid stroke="#ffffff" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: '#fff' }} />
                <YAxis tick={{ fill: '#fff' }} />
                <Tooltip />
                <Legend />
                {Object.keys(combinedData).map(deviceId => (
                     <Line
                     key={deviceId}
                     type="monotone"
                     dataKey={deviceId}
                     name={deviceMetaMap[deviceId]?.position || deviceId}
                     stroke={deviceMetaMap[deviceId]?.color || '#8884d8'}
                     connectNulls
                   
                   />
               
                ))}
              <Brush/>
              </LineChart>
              
            </ResponsiveContainer>
          )}
        </Paper>
      </CardContent>
    </Card>
  );
};

export default SandboxTrainDetail;
